import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signIn } from "src/components/organisms/auth";
import { ILoggedInUser, useAuth } from "./auth-context";
import { TokenHelper } from "./token-helper";
import ButtonPro, {
	ButtonVariant,
} from "src/components/atoms/primary-button/primary-button";
import InputBox from "src/components/atoms/v2/inputBox/inputBox";
import Logo from "src/components/molecules/logo/Logo";
import LeftSection from "./left-section";
import { validateEmail } from "src/helpers/email-address-helper";
import LoadingModal from "src/components/molecules/loading/LoadingModal";
import { ErrorDisplay } from "src/components/atoms/error/error";
import { PermissionBagService } from "./permissionBagService";
import HyperLink from "src/components/atoms/hyper-link/hyper-link";

const Login: React.FC = () => {
	const [businessEmail, setBusinessEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const { login, logout, setCognitoUserContext: setCognitoUser } = useAuth();
	const navigate = useNavigate();
	const tokenHelper = useRef<TokenHelper | null>(null);
	const permissionBag = useRef<PermissionBagService | null>(null);

	useEffect(() => {
		tokenHelper.current = new TokenHelper();
		logout(); // this is to make sure if someone clicks on browser back button they are logged out.
	}, []);

	const handleForgotPasswordClick = () => {
		if (validateEmail(businessEmail)) {
			navigate("/forgot-password", {
				state: { businessEmail },
			});
		} else {
			navigate(`/forgot-password`);
		}
	};

	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		setError("");

		if (!businessEmail || !password) {
			setError("Missing credentials");
			return;
		}

		setLoading(true);

		signIn(businessEmail, password, async (result) => {
			switch (result.status) {
				case "NEW_PASSWORD_REQUIRED":
					setLoading(false);
					setCognitoUser(result.cognitoUser);
					navigate("/set-new-password", {
						state: { businessEmail },
					});
					return;

				case "CHALLENGE":
					setLoading(false);
					console.log("CHALLENGE", result);
					setCognitoUser(result.cognitoUser);
					switch (result.challengeName) {
						case "CUSTOM_CHALLENGE":
							navigate("/verify-email-otp", {
								state: {
									businessEmail,
									challengeParameters:
										result.challengeParameters,
								},
							});
							break;
						case "SOFTWARE_TOKEN_MFA":
							navigate("/verify-totp", {
								state: {
									businessEmail,
									challengeParameters:
										result.challengeParameters,
								},
							});
							break;
						case "SELECT_MFA_TYPE":
							navigate("/select-mfa", {
								state: {
									businessEmail,
									challengeParameters:
										result.challengeParameters,
								},
							});
							break;
						case "MFA_SETUP":
							navigate("/setup-mfa", {
								state: {
									businessEmail,
									challengeParameters:
										result.challengeParameters,
								},
							});
							break;
						default:
							setError(
								`Unsupported challenge: ${result.challengeName}`
							);
							break;
					}
					return;

				case "ERROR":
					setLoading(false);
					// console.log("ERROR", result);
					if (result.error.code === "UserNotConfirmedException") {
						navigate("/verify", {
							state: { businessEmail, password },
						});
					} else {
						// console.error("Auth Error:", result.error);
						setError(
							result.error.message || JSON.stringify(result.error)
						);
					}
					return;

				case "SUCCESS":
					try {
						const tokenProps =
							await tokenHelper.current?.decodeToken(
								result.session.getAccessToken().getJwtToken()
							);

						permissionBag.current = new PermissionBagService(
							result.session.getAccessToken().getJwtToken()
						);

						const permissions =
							await permissionBag.current.getPermissionBag(
								result.session.getAccessToken().payload.role
							);

						if (!permissions?.permissions.length) {
							setError("Unable to get user permissions");
							navigate("/error", {
								state: {
									error: "Unable to get user permissions",
								},
							});
							return;
						}

						const loggedInUser: ILoggedInUser = {
							id: result.session.getIdToken().payload.sub,
							givenName:
								result.session.getIdToken().payload.given_name,
							familyName:
								result.session.getIdToken().payload.family_name,
							username: businessEmail,
							phoneNumber:
								result.session.getIdToken().payload
									.phone_number,
							ex_user_id:
								result.session.getAccessToken().payload
									.ex_user_id,
							accessToken: result.session
								.getAccessToken()
								.getJwtToken(),
							num: result.session.getAccessToken().payload.num,
							accountId: tokenProps?.account_id ?? "",
							email: tokenProps?.email ?? "",
							reseller_id: tokenProps?.reseller_id,
							role: tokenProps?.role,
							exp: result.session.getAccessToken().payload.exp,
							permissionBag: permissions,
							has_multiple_accounts:
								tokenProps?.has_multiple_accounts ?? false,
						};

						login(loggedInUser);

						if (!loggedInUser.accountId) {
							navigate("/select-account");
						} else {
							navigate("/");
						}
					} catch (tokenError) {
						setLoading(false);
						console.error("Token handling failed:", tokenError);
						setError(
							"Authentication succeeded but token processing failed."
						);
					}
					return;
			}
		});
	};

	return (
		<>
			{/* main */}
			<div className="md:bg-app-gradient flex items-start justify-center md:items-center md:justify-center h-screen">
				<div className="flex">
					<LeftSection></LeftSection>
					{/* right section */}
					<div className="bg-transparent md:bg-white md:flex md:flex-col md:items-center md:justify-evenly w-screen max-w-[550px] px-12 pt-6 rounded-r-2xl md:shadow-[0_30px_50px_rgba(0,0,0,.5)]">
						<div className="flex flex-col items-center justify-center gap-4 md:gap-2">
							<div className="md:hidden py-4">
								<Logo></Logo>
							</div>
							<span className="font-Poppins tracking-wider bg-gradient-to-bl from-purple-600 to-red-800 text-4xl md:text-6xl font-bold lg:text-4xl bg-clip-text text-transparent uppercase drop-shadow-2xl">
								Login
							</span>
						</div>
						<form
							className="flex flex-col justify-center items-center w-full"
							onSubmit={handleSubmit}
						>
							<div className="flex flex-col items-center justify-between gap-2 mt-6 w-full">
								<InputBox
									value={businessEmail}
									onChange={(value) => {
										setBusinessEmail(value);
									}}
									label="User name"
									type={"text"}
									required
								></InputBox>

								<InputBox
									value={password}
									onChange={(value) => {
										setPassword(value);
									}}
									label="Password"
									type={"password"}
									required
								></InputBox>

								<div className="flex justify-end text-right w-full">
									<HyperLink
										href={"#"}
										label={"forgot password?"}
										onClick={() =>
											handleForgotPasswordClick()
										}
									></HyperLink>
								</div>
								{error && (
									<ErrorDisplay error={error}></ErrorDisplay>
								)}
								<div className="flex flex-col items-center justify-center gap-2 pt-4 w-full">
									<ButtonPro
										type="submit"
										label={"Login"}
									></ButtonPro>

									<div className="flex flex-row flex-1">
										<div className="border-t-2 border-gray-300 w-1/2"></div>
										<span className="mx-4 font-semibold text-gray-900">
											Or
										</span>
										<div className="border-t-2 border-gray-300 w-1/4"></div>
									</div>

									<ButtonPro
										variant={ButtonVariant.Secondary}
										label={"Create new Account"}
										onClick={() => navigate("/signup")}
									></ButtonPro>
									<LoadingModal
										open={loading}
										message="Getting you in..."
									/>
								</div>
							</div>
						</form>
					</div>
				</div>
				{/* <Footer></Footer> */}
			</div>
		</>
	);
};

export default Login;
